
import * as React from 'react';
import {
  Selection,
  ISelection,
  ISelectionZoneProps,
  SelectionMode,
  ISelectionOptions,
  IObjectWithKey
} from 'office-ui-fabric-react/lib/Selection';
import { injectInitialProps } from '../../utilities/components/Props';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { selectItems } from '../../operations/selectItems';
import { connectToItemCache } from './connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { currentSelectionFacet } from '../../facets/ScopeFacets';
import { AutoKeyFacet } from '../../models/AutoKeyFacet';
import { IComponentDecorator, IDecoratedProps, IDecoratedComponentType } from '../../utilities/components/Decorators';
import { readItems } from '../../operations/readItems';
import { itemSetSelectionControl, IItemSetSelectionMode } from '../../controls/itemSetSelectionControl';
import { Fragment } from '../../utilities/components/Fragment';
import { KeyboardShortcut } from '../keyboard/KeyboardShortcut';
import { Facet } from '../../models/Facet';
import KeyboardMapStrings from '../keyboard/KeyboardMap.resx';
import { ICommand } from '../../models/commands/ICommand';
import { getItemsScope } from '../../models/itemsScope/getItemsScope';
import { createMemoizer } from '../../utilities/Memoizer';
import { withActionInvoker } from '../decorators/withActionInvoker';
import { forceStoreUpdate } from '../../store/enhancers/NextStoreUpdateEnhancer';
import { Omit } from '../../utilities/Omit';

function convertFabricToItemsView(selectionMode: SelectionMode): IItemSetSelectionMode {
  switch (selectionMode) {
    case SelectionMode.single:
      return 'single';
    case SelectionMode.none:
      return 'none';
    default:
    case SelectionMode.multiple:
      return 'multiple';
  }
}

const ITEMS_VIEW_TO_FABRIC: { [K in IItemSetSelectionMode]: SelectionMode; } = {
  'multiple': SelectionMode.multiple,
  'single': SelectionMode.single,
  'none': SelectionMode.none
};

const selectionKeyFacet = new AutoKeyFacet('selection');

const selectAllShortcut: ICommand = {
  key: new Facet('SelectAll').id,
  automationId: 'SelectAll',
  keytipProps: {
    keySequences: ['CtrlA', 'MetaA'],
    content: KeyboardMapStrings.selectAllDesc
  }
};

const deselectAllShortcut: ICommand = {
  key: new Facet('DeselectAll').id,
  automationId: 'DeselectAll',
  keytipProps: {
    keySequences: ['CtrlD', 'MetaD'],
    content: KeyboardMapStrings.deselectAllDesc
  }
};

const DEFAULT_SELECTION_ZONE_PROPS = {} as ISelectionZoneProps;

const selectionZonePropsMemoizer = createMemoizer(mergeSelectionZoneProps);

/**
 * @public
 */
export interface ISelectionProps {
  selectionZoneProps?: Partial<ISelectionZoneProps>;
}

/**
 * @public
 */
export interface ISelectionInjectedProps {
  selectionZoneProps?: Omit<ISelectionZoneProps, 'componentRef'>;
  onItemInvoked?: ISelectionZoneProps['onItemInvoked'];
}

/**
 * @public
 */
export interface ISelectionComponentInjectedProps {
  selection?: ISelectionZoneProps['selection'];
}

/**
 * @public
 */
export type IDecorateConnectToSelectionProps<TProps extends TInjectedProps & ISelectionInjectedProps, TInjectedProps, TOwnProps = {}> =
  IDecoratedProps<TProps, TInjectedProps & ISelectionInjectedProps, TOwnProps & ISelectionProps>;

/**
 * Higher-order component which binds a selection model to the lifetime of the target,
 * keeping it in sync with ItemsScope.
 * @internal
 */
export function withItemSetSelection<TInjectedProps, TOwnProps = {}>(
  mapSelectionToProps: (selection: ISelection, props: TOwnProps) => TInjectedProps
): IComponentDecorator<TInjectedProps & ISelectionInjectedProps, TOwnProps & ISelectionProps>;
/**
 * Higher-order component which binds a selection model to the lifetime of the target,
 * keeping it in sync with ItemsScope.
 * @internal
 */
export function withItemSetSelection():
  IComponentDecorator<ISelectionComponentInjectedProps & ISelectionInjectedProps, ISelectionProps>;
/**
 * Higher-order component which binds a selection model to the lifetime of the target,
 * keeping it in sync with ItemsScope.
 * @internal
 */
export function withItemSetSelection<TInjectedProps, TOwnProps = {}>(
  mapSelectionToProps?: (selection: ISelection, props: TOwnProps) => TInjectedProps
): IComponentDecorator<TInjectedProps & ISelectionComponentInjectedProps & ISelectionInjectedProps, TOwnProps & ISelectionProps> {
  return <P extends TInjectedProps & ISelectionComponentInjectedProps & ISelectionInjectedProps>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P,
    TInjectedProps & ISelectionComponentInjectedProps & ISelectionInjectedProps,
    TOwnProps & ISelectionProps> => {
    const selectionKey = selectionKeyFacet.serializeNext();

    const Component = component;

    interface IDeselectorProps {
      selection: Selection;
      currentSelectionKey: string | undefined;
    }

    function initializeSelectionProps(props: {
      itemsScopeItemKey: string;
      itemCacheDispatch: IItemCacheDispatch;
    } & ISelectionProps & Pick<ISelectionOptions, 'canSelectItem'>): Pick<IDeselectorProps, 'selection'> {
      const {
        itemsScopeItemKey,
        selectionZoneProps: {
          selectionMode = SelectionMode.multiple
        } = {},
        itemCacheDispatch
      } = props;

      const selection = new Selection({
        onSelectionChanged: () => {
          const selectedItemKeys = (selection.getSelection() as {
            key: string;
          }[]).map((item: {
            key: string;
          }) => item.key);

          const currentSelection = itemCacheDispatch(readItems((itemCache: IItemCache) => {
            return itemCache.demandItemFacet(currentSelectionFacet, itemsScopeItemKey);
          }));

          // When we clear selection from non-active selection in the Deselector, it will also trigger onSelectionChanged.
          // But for that case, we should not dispatch selectItems. Otherwise it will set selectedItemKeys to empty.
          if (selectedItemKeys.length !== 0 || (currentSelection && selectionKey === currentSelection.selectionKey)) {
            itemCacheDispatch(selectItems({
              itemsScopeItemKey: itemsScopeItemKey,
              selectedItemKeys: selectedItemKeys,
              currentSelectionKey: selectionKey
            }));

            itemCacheDispatch(forceStoreUpdate());
          }
        },
        selectionMode: selectionMode,
        canSelectItem: props.canSelectItem
      });

      return {
        selection: selection
      };
    }

    /**
     * Higher-order component which will deselect all selected items when
     * the active selection key is changed and it is not current active selection.
     * This is useful to ensure only one selection has selected items.
     */
    class Deselector extends React.PureComponent<IDeselectorProps & P> {
      public render(): JSX.Element {
        const {
          selection,
          currentSelectionKey,
          ...props
        } = this.props as IDeselectorProps;

        const selectAllKeytipProps = selectAllShortcut.keytipProps;
        const deselectAllKeytipProps = deselectAllShortcut.keytipProps;

        return (
          <Fragment>
            <Component
              { ...props }
              { ...(mapSelectionToProps ? mapSelectionToProps(selection, this.props as {} as TOwnProps) : {
                selection
              }) as P }
            />
            { (selectAllKeytipProps ?
              <KeyboardShortcut
                key={ selectAllShortcut.key }
                cmdKey={ selectAllKeytipProps.keySequences[0] }
                keySequences={ selectAllKeytipProps.keySequences }
                description={ selectAllKeytipProps.content }
                onExecute={ this._onSelectAllShortcut }
              /> : null) }
            { (deselectAllKeytipProps ?
              <KeyboardShortcut
                key={ deselectAllShortcut.key }
                cmdKey={ deselectAllKeytipProps.keySequences[0] }
                keySequences={ deselectAllKeytipProps.keySequences }
                description={ deselectAllKeytipProps.content }
                onExecute={ this._onDeselectAllShortcut }
              /> : null) }
          </Fragment>
        );
      }

      public componentDidUpdate(previousProps: IDeselectorProps): void {
        const {
          currentSelectionKey,
          selection
        } = this.props;

        if (currentSelectionKey !== previousProps.currentSelectionKey &&
          currentSelectionKey as string !== selectionKey) {
          if (selection.getSelectedCount()) {
            // Only clear the selection when it has selected items.
            selection.setAllSelected(false);
          }
        }
      }

      public componentWillUnmount(): void {
        const {
          currentSelectionKey,
          selection
        } = this.props;

        if (currentSelectionKey as string === selectionKey) {
          if (selection.getSelectedCount()) {
            selection.setAllSelected(false);
          }
        }
      }

      private _onSelectAllShortcut = (): void => {
        const { selection } = this.props;
        if (selection && !selection.isAllSelected()) {
          selection.setAllSelected(true);
        }
      }

      private _onDeselectAllShortcut = (): void => {
        const { selection } = this.props;
        if (selection) {
          selection.setAllSelected(false);
        }
      }
    }

    const WithInitialProps = injectInitialProps(initializeSelectionProps)(Deselector);

    function mapItemCacheSelectionProps(
      itemCache: IItemCache,
      props: ISelectionProps
    ): Pick<IDeselectorProps, 'currentSelectionKey'> & ISelectionProps & Pick<ISelectionOptions, 'canSelectItem'> & {
      itemsScopeItemKey: string;
    } {
      const {
        selectionZoneProps = DEFAULT_SELECTION_ZONE_PROPS
      } = props;

      const {
        selectionMode: originalSelectionMode = SelectionMode.multiple
      } = selectionZoneProps;

      const {
        itemsScopeItemKey
      } = getItemsScope(itemCache);

      if (!itemsScopeItemKey) {
        throw new Error('connectToSelection must be used within an ItemsScope.');
      }

      const { dispatch } = itemCache;

      const currentSelection = itemCache.demandItemFacet(currentSelectionFacet, itemsScopeItemKey);

      const controlHandler = itemCache.demandItemFacet(itemSetSelectionControl, undefined);

      const {
        selectionMode: itemSetSelectionMode,
        canSelectItem: itemSetCanSelectItem
      } = itemSetSelectionControl.evaluate(controlHandler)(itemCache, {
        itemsScopeItemKey,
        selectionMode: convertFabricToItemsView(originalSelectionMode)
      });

      const canSelectItem = itemSetCanSelectItem ? (item: IObjectWithKey) => {
        if (!item) {
          return false;
        }

        return dispatch(readItems((itemCache2: IItemCache) => {
          return itemSetCanSelectItem(itemCache2, { itemKey: `${item.key}` });
        }));
      } : undefined;

      return {
        currentSelectionKey: currentSelection && currentSelection.selectionKey,
        itemsScopeItemKey,
        selectionZoneProps: selectionZonePropsMemoizer(selectionZoneProps)(ITEMS_VIEW_TO_FABRIC[itemSetSelectionMode]),
        canSelectItem: canSelectItem
      };
    }

    const WithItemCacheSelection = connectToItemCache(mapItemCacheSelectionProps)(WithInitialProps);

    const WithInvoker = withActionInvoker()(WithItemCacheSelection);

    WithInvoker.displayName = 'WithItemSetSelection';

    return WithInvoker as {} as IDecoratedComponentType<P,
      TInjectedProps & ISelectionComponentInjectedProps & ISelectionInjectedProps,
      TOwnProps & ISelectionProps>;
  };
}

function mergeSelectionZoneProps(
  selectionZoneProps: Partial<ISelectionZoneProps>
): (selectionMode: SelectionMode) => Partial<ISelectionZoneProps> {
  const map: {
    [K in keyof typeof SelectionMode]?: Partial<ISelectionZoneProps>;
  } = {};

  return (selectionMode: SelectionMode): Partial<ISelectionZoneProps> => {
    return map[selectionMode] || (map[selectionMode] = {
      ...selectionZoneProps,
      selectionMode
    });
  };
}
